import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/src/lib/axios/axios';
import { MESSAGING_API_BASE, messagingQueryKeys } from '../config';
import { MarkAsReadBody } from '../types/backend';

export type MarkAsReadDTO = {
  channelId: number;
  messageId: number;
};

const markAsRead = async (
  data: MarkAsReadDTO
): Promise<{ success: boolean }> => {
  const { channelId, messageId } = data;

  const body: MarkAsReadBody = {
    'message-id': messageId,
  };

  return axios.post(`${MESSAGING_API_BASE}/channels/${channelId}/read`, body);
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MarkAsReadDTO) => markAsRead(data),
    onSuccess: (_, variables) => {
      // Optimistically update unread counts immediately
      queryClient.setQueryData(
        messagingQueryKeys.unreadCounts(),
        (old: Record<number, number> | undefined) => {
          if (!old) return old;
          // Marking a specific message as read implies the backend marks that
          // message and all previous messages as read for the current user.
          // Therefore, optimistically set unread count to 0 for this channel.
          return { ...old, [variables.channelId]: 0 };
        }
      );

      // NOTE: We rely on socket events (MESSAGING_UNREAD_COUNT and MESSAGING_MESSAGE_READ)
      // to sync the correct state from the backend. The optimistic updates above provide
      // instant feedback. We don't invalidate queries here to avoid race conditions and
      // duplicate API calls.

      // NOTE: We also don't invalidate channels list to avoid unnecessary refetches
      // Unread badges will update via the optimistic update above + socket events
    },
  });
};
